import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  ClearIcon,
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useAtom } from 'jotai';
import moment from 'moment';
import { ChangeEvent, useEffect, useState, useMemo, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';

import {
  InvoiceFilters,
  DocumentType,
  PaymentStatus,
  PaymentChannel,
  EntryType,
  CurrencyDropDown
} from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { DOCUMENT_CONFIGS } from 'utils/config/documentEntries';
import { DATE_FORMATS } from 'utils/constants/constants';
import { REIMBURSEMENT_FILTER_OPTIONS } from 'utils/constants/filters';
import {
  DOCUMENT_TYPES_TRANSLATION_KEYS,
  PAYMENT_CHANNELS_TRANSLATION_KEYS,
  PAYMENT_STATUSES_TRANSLATION_KEYS
} from 'utils/constants/invoices';
import { FilterType } from 'utils/enums/FilterType';
import {
  shouldDisableEndDate,
  shouldDisableStartDate,
  validateDateValue,
  validateDateRange
} from 'utils/helpers/dates';
import { getInvoicesFiltersAtomByType } from 'utils/helpers/filtersHelpers';
import { processMoneyInput } from 'utils/helpers/moneyHelper';
import { InvoicesFiltersForm } from 'utils/interfaces/InvoiceProps';
import { getFormFilters } from 'utils/mappers/invoice';
import { AppRoutesEnum } from 'utils/routes';

import { labelSx } from 'styles/components/Common';
import { filterFieldSizeMap } from 'styles/components/Filters';

import { companyAtom, filterTypeAtom } from 'state/state';

interface InvoicesFilterPanelProps {
  filtersData?: InvoiceFilters;
  areFiltersVisible?: boolean;
  isOnAllCompanies?: boolean;
  handleApplyFilters: (data: InvoicesFiltersForm | null) => void;
  isOnRevenue?: boolean;
  stateRoute?: AppRoutesEnum;
  entryType: EntryType;
}

export const InvoicesFilterPanel = ({
  filtersData,
  areFiltersVisible,
  isOnAllCompanies,
  handleApplyFilters,
  isOnRevenue,
  stateRoute,
  entryType
}: InvoicesFilterPanelProps) => {
  const documentConfig = DOCUMENT_CONFIGS[entryType];

  const [company] = useAtom(companyAtom);
  const [filterType] = useAtom(filterTypeAtom);
  const [filters] = useAtom(
    getInvoicesFiltersAtomByType(filterType || FilterType.Initial, stateRoute)
  );
  const { control, handleSubmit, reset, getValues, setValue, watch } =
    useForm<InvoicesFiltersForm>({
      defaultValues: documentConfig.defaultFilterValues,
      values: getFormFilters(filters)
    });

  const { translate } = useTranslations();

  const [counterPartyOptions, setCounterPartyOptions] = useState(
    filtersData || {}
  );
  const [isInvoiceDateFromOpen, setInvoiceDateFromOpen] =
    useState<boolean>(false);
  const [isInvoiceDateToOpen, setOpenInvoiceDateTo] = useState<boolean>(false);

  const [isUploadedDateFromOpen, setIsUploadedDateFromOpen] =
    useState<boolean>(false);
  const [isUploadedDateToOpen, setIsUploadedDateToOpen] =
    useState<boolean>(false);
  const [shouldShowMinMaxError, setShouldShowMinMaxError] = useState(false);
  const [accountStatus, setAccountStatus] = useState<string>(
    getFormFilters(filters).accounted || ''
  );
  const [isReimbursement, setIsReimbursement] = useState<string>(
    getFormFilters(filters).isReimbursement || ''
  );
  const [tags, setTags] = useState<string[]>(filters.tags || []);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [dateErrors, setDateErrors] = useState({
    invoiceDateFrom: '',
    invoiceDateTo: '',
    uploadedDateFrom: '',
    uploadedDateTo: ''
  });

  const watchedFormValues = watch();

  const { getCurrencies } = useAdditionalFieldsController();
  const [currencies, setCurrencies] = useState<CurrencyDropDown[]>();

  const fetchCurrencies = useCallback(async () => {
    const currenciesResponse = await getCurrencies();
    setCurrencies(currenciesResponse);
  }, [getCurrencies]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  useEffect(() => {
    setCounterPartyOptions(filtersData || {});
    setAvailableTags(filtersData?.tags || []);
    reset(documentConfig.defaultFilterValues);
  }, [filtersData]);

  const filterFields = isOnAllCompanies
    ? documentConfig.allCompaniesFiltersFields
    : documentConfig.filtersFields;

  const handleClearAccountStatus = () => {
    setAccountStatus('');
    setValue('accounted', '');
  };

  const handleClearIsReimbursement = () => {
    setValue('isReimbursement', '');
    setIsReimbursement('');
  };

  const validateDate = (dateValue: Date | null, fieldName: string): string => {
    const dateValidationError = validateDateValue(dateValue);
    if (dateValidationError) return dateValidationError;

    if (fieldName === 'invoiceDateTo') {
      const fromDate = getValues('invoiceDateFrom');
      const toDate = getValues('invoiceDateTo');
      return validateDateRange(fromDate, toDate);
    }

    if (fieldName === 'uploadedDateTo') {
      const fromDate = getValues('uploadedDateFrom');
      const toDate = getValues('uploadedDateTo');
      return validateDateRange(fromDate, toDate);
    }

    return '';
  };

  const handleDateChange = (
    value: Date | null,
    fieldName: string,
    onChange: (value: Date | null) => void
  ) => {
    onChange(value);

    const datePairs = {
      invoiceDateFrom: 'invoiceDateTo',
      invoiceDateTo: 'invoiceDateFrom',
      uploadedDateFrom: 'uploadedDateTo',
      uploadedDateTo: 'uploadedDateFrom'
    } as const;

    const error = validateDate(value, fieldName);
    const pairedField = datePairs[fieldName as keyof typeof datePairs];
    const pairedFieldError = pairedField
      ? validateDate(getValues(pairedField), pairedField)
      : '';

    setDateErrors((prev) => ({
      ...prev,
      [fieldName]: error,
      ...(pairedField && { [pairedField]: pairedFieldError })
    }));
  };

  const hasValidationErrors = useMemo(() => {
    return (
      Object.values(dateErrors).some((error) => error !== '') ||
      shouldShowMinMaxError
    );
  }, [dateErrors, shouldShowMinMaxError]);

  const hasFiltersApplied = useMemo(() => {
    const defaultValues = documentConfig.defaultFilterValues;

    return Object.keys(watchedFormValues).some((key) => {
      const currentValue = watchedFormValues[key as keyof InvoicesFiltersForm];
      const defaultValue = defaultValues[key as keyof InvoicesFiltersForm];

      if (Array.isArray(currentValue)) {
        return currentValue.length > 0;
      }

      if (typeof currentValue === 'string') {
        return currentValue !== '' && currentValue !== defaultValue;
      }

      if (currentValue instanceof Date || moment.isMoment(currentValue)) {
        return currentValue !== null && currentValue !== defaultValue;
      }

      return (
        currentValue !== defaultValue &&
        currentValue !== null &&
        currentValue !== undefined
      );
    });
  }, [watchedFormValues, documentConfig.defaultFilterValues]);

  const isApplyButtonDisabled = hasValidationErrors || !hasFiltersApplied;
  const companyCurrency = isOnAllCompanies
    ? undefined
    : {
        currency:
          currencies?.find((c) => c.id === company?.currencyId)?.isoCode || ''
      };

  return (
    <Paper
      elevation={4}
      className={`filters-container ${!areFiltersVisible && 'hidden'}`}
    >
      <Grid container>
        <form
          className={`filters ${!areFiltersVisible && 'hidden'}`}
          onSubmit={handleSubmit(handleApplyFilters)}
        >
          <Grid sx={{ overflow: 'auto' }} container spacing={1}>
            {filterFields.map((filter) => (
              <Grid
                key={filter.name}
                size={{
                  xs: 12,

                  sm: filterFieldSizeMap[isOnRevenue ? 'revenue' : 'expense'][
                    isOnAllCompanies ? 'allCompanies' : 'singleCompany'
                  ]
                }}
              >
                <Controller
                  name={filter.name as keyof InvoiceFilters}
                  control={control}
                  render={({ field }) => (
                    <FormControl sx={{ width: '100%' }} size="small" fullWidth>
                      <Autocomplete
                        {...field}
                        multiple
                        size="small"
                        options={
                          counterPartyOptions[
                            filter.name as keyof InvoiceFilters
                          ] || []
                        }
                        disableCloseOnSelect
                        getOptionLabel={(option) => option || ''}
                        onChange={(e, value) => {
                          field.onChange(value);
                          setCounterPartyOptions((previousValue) => {
                            const filteredValues = previousValue[
                              filter.name as keyof InvoiceFilters
                            ]?.filter((item) => !value.some((v) => v === item));
                            return {
                              ...previousValue,
                              [filter.name]: [
                                ...value,
                                ...(filteredValues || [])
                              ]
                            };
                          });
                        }}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={
                                <CheckBoxOutlineBlankIcon fontSize="small" />
                              }
                              checkedIcon={<CheckBoxIcon fontSize="small" />}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option}
                          </li>
                        )}
                        renderValue={(value, getItemProps) =>
                          value
                            .slice(0, 1)
                            .map((option, index) => {
                              const tagProps = getItemProps({ index });
                              const { key, ...rest } = tagProps;
                              return (
                                <Chip
                                  key={key}
                                  sx={{ width: '65%' }}
                                  label={option}
                                  {...rest}
                                  disabled={index > 1}
                                  size="small"
                                />
                              );
                            })
                            .concat(
                              value.length > 1
                                ? [
                                    <Chip
                                      size="small"
                                      key="more"
                                      label={`+${value.length - 1} ${translate(
                                        'labels.more'
                                      )}`}
                                    />
                                  ]
                                : []
                            )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate(filter.translationKey)}
                            placeholder={translate(filter.translationKey)}
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />
              </Grid>
            ))}

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Grid
                size={{
                  xs: 12,
                  sm: 2
                }}
              >
                <Controller
                  name="invoiceDateFrom"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const hasError = !!dateErrors.invoiceDateFrom;
                    return (
                      <FormControl error={hasError} sx={{ width: '100%' }}>
                        <DatePicker
                          sx={{ width: '100%' }}
                          format={DATE_FORMATS.displayedDateFormat}
                          open={isInvoiceDateFromOpen}
                          shouldDisableDate={(date) =>
                            shouldDisableStartDate(
                              date,
                              getValues('invoiceDateTo')
                            )
                          }
                          disableFuture
                          label={translate('labels.invoiceDateFrom')}
                          value={value ? moment(value) : null}
                          onClose={() => setInvoiceDateFromOpen(false)}
                          onChange={(newValue) =>
                            handleDateChange(
                              newValue?.toDate() || null,
                              'invoiceDateFrom',
                              onChange
                            )
                          }
                          slotProps={{
                            textField: {
                              size: 'small',
                              name: 'invoiceDateFrom',
                              onClick: () => setInvoiceDateFromOpen(true),
                              error: hasError,
                              helperText: dateErrors.invoiceDateFrom
                                ? translate(dateErrors.invoiceDateFrom)
                                : '',
                              InputLabelProps: {
                                sx: labelSx
                              }
                            },
                            openPickerButton: {
                              onClick: () => setInvoiceDateFromOpen(true)
                            }
                          }}
                        />
                      </FormControl>
                    );
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 2
                }}
              >
                <Controller
                  name="invoiceDateTo"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const hasError = !!dateErrors.invoiceDateTo;
                    return (
                      <FormControl error={hasError} sx={{ width: '100%' }}>
                        <DatePicker
                          sx={{ width: '100%' }}
                          format={DATE_FORMATS.displayedDateFormat}
                          shouldDisableDate={(date) =>
                            shouldDisableEndDate(
                              date,
                              getValues('invoiceDateFrom')
                            )
                          }
                          disableFuture
                          open={isInvoiceDateToOpen}
                          label={translate('labels.invoiceDateTo')}
                          value={value ? moment(value) : null}
                          onClose={() => setOpenInvoiceDateTo(false)}
                          onChange={(newValue) =>
                            handleDateChange(
                              newValue?.toDate() || null,
                              'invoiceDateTo',
                              onChange
                            )
                          }
                          slotProps={{
                            textField: {
                              size: 'small',
                              name: 'invoiceDateTo',
                              onClick: () => setOpenInvoiceDateTo(true),
                              error: hasError,
                              helperText: dateErrors.invoiceDateTo
                                ? translate(dateErrors.invoiceDateTo)
                                : '',
                              InputLabelProps: {
                                sx: labelSx
                              }
                            },
                            openPickerButton: {
                              onClick: () => setOpenInvoiceDateTo(true)
                            }
                          }}
                        />
                      </FormControl>
                    );
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 2
                }}
              >
                <Controller
                  name="uploadedDateFrom"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const hasError = !!dateErrors.uploadedDateFrom;
                    return (
                      <FormControl error={hasError} sx={{ width: '100%' }}>
                        <DatePicker
                          sx={{ width: '100%' }}
                          format={DATE_FORMATS.displayedDateFormat}
                          open={isUploadedDateFromOpen}
                          shouldDisableDate={(date) =>
                            shouldDisableStartDate(
                              date,
                              getValues('uploadedDateTo')
                            )
                          }
                          disableFuture
                          label={translate('labels.uploadedDateFrom')}
                          value={value ? moment(value) : null}
                          onClose={() => setIsUploadedDateFromOpen(false)}
                          onChange={(newValue) =>
                            handleDateChange(
                              newValue?.toDate() || null,
                              'uploadedDateFrom',
                              onChange
                            )
                          }
                          slotProps={{
                            textField: {
                              size: 'small',
                              name: 'uploadedDateFrom',
                              onClick: () => setIsUploadedDateFromOpen(true),
                              error: hasError,
                              helperText: dateErrors.uploadedDateFrom
                                ? translate(dateErrors.uploadedDateFrom)
                                : '',
                              InputLabelProps: {
                                sx: labelSx
                              }
                            },
                            openPickerButton: {
                              onClick: () => setIsUploadedDateFromOpen(true)
                            }
                          }}
                        />
                      </FormControl>
                    );
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 2
                }}
              >
                <Controller
                  name="uploadedDateTo"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const hasError = !!dateErrors.uploadedDateTo;
                    return (
                      <FormControl error={hasError} sx={{ width: '100%' }}>
                        <DatePicker
                          sx={{ width: '100%' }}
                          format={DATE_FORMATS.displayedDateFormat}
                          shouldDisableDate={(date) =>
                            shouldDisableEndDate(
                              date,
                              getValues('uploadedDateFrom')
                            )
                          }
                          disableFuture
                          open={isUploadedDateToOpen}
                          label={translate('labels.uploadedDateTo')}
                          value={value ? moment(value) : null}
                          onClose={() => setIsUploadedDateToOpen(false)}
                          onChange={(newValue) =>
                            handleDateChange(
                              newValue?.toDate() || null,
                              'uploadedDateTo',
                              onChange
                            )
                          }
                          slotProps={{
                            textField: {
                              size: 'small',
                              name: 'uploadedDateTo',
                              onClick: () => setIsUploadedDateToOpen(true),
                              error: hasError,
                              helperText: dateErrors.uploadedDateTo
                                ? translate(dateErrors.uploadedDateTo)
                                : '',
                              InputLabelProps: {
                                sx: labelSx
                              }
                            },
                            openPickerButton: {
                              onClick: () => setIsUploadedDateToOpen(true)
                            }
                          }}
                        />
                      </FormControl>
                    );
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}
              >
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      size="small"
                      sx={{ '.MuiInputLabel-root': { ...labelSx } }}
                      className="tags-autocomplete"
                      fullWidth
                      disableCloseOnSelect
                      multiple
                      options={availableTags}
                      value={tags}
                      onChange={(event, newValue) => {
                        const newTags = newValue
                          .map((tag) => tag.trim())
                          .filter((tag) => tag);
                        setTags(newTags);
                        field.onChange(newTags);
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option}
                        </li>
                      )}
                      renderValue={(value, getItemProps) =>
                        value
                          .slice(0, 1)
                          .map((option, index) => {
                            const tagProps = getItemProps({ index });
                            const { key, ...rest } = tagProps;
                            return (
                              <Chip
                                key={key}
                                sx={{ width: '65%' }}
                                label={option}
                                {...rest}
                                disabled={index > 1}
                                size="small"
                              />
                            );
                          })
                          .concat(
                            value.length > 1
                              ? [
                                  <Chip
                                    size="small"
                                    key="more"
                                    label={`+${value.length - 1} ${translate(
                                      'labels.more'
                                    )}`}
                                  />
                                ]
                              : []
                          )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={translate('labels.tags')}
                          placeholder={translate('labels.tags')}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              {documentConfig.allowFilterFields.allowAccounted && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 2
                  }}
                >
                  <Controller
                    name="accounted"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel sx={labelSx}>
                          {translate('labels.accountedFilter')}
                        </InputLabel>
                        <Select
                          {...field}
                          value={accountStatus}
                          sx={{ textAlign: 'left' }}
                          size="small"
                          label={translate('labels.accountedFilter')}
                          onChange={(e) => {
                            setAccountStatus(e.target.value as string);
                            field.onChange(e.target.value);
                          }}
                          fullWidth
                          endAdornment={
                            field.value ? (
                              <InputAdornment
                                position="end"
                                sx={{ marginRight: 2.5 }}
                              >
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={handleClearAccountStatus}
                                >
                                  <ClearIcon sx={{ fontSize: '1.3rem' }} />
                                </IconButton>
                              </InputAdornment>
                            ) : null
                          }
                        >
                          <MenuItem value="true">
                            {translate('labels.bookedStatus')}
                          </MenuItem>
                          <MenuItem value="false">
                            {translate('labels.notBooked')}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
              <Grid
                size={{
                  xs: 12,
                  sm: 2
                }}
              >
                <Controller
                  name="documentTypes"
                  control={control}
                  render={({ field }) => (
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={labelSx}>
                        {translate('labels.documentType')}
                      </InputLabel>
                      <Select
                        {...field}
                        multiple
                        value={field.value || []}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        sx={{ textAlign: 'left' }}
                        label={translate('labels.documentType')}
                        renderValue={(selected) => {
                          const selectedDocumentTypes =
                            selected as DocumentType[];

                          if (selectedDocumentTypes.length === 0) {
                            return null;
                          }
                          if (selectedDocumentTypes.length === 1) {
                            return (
                              <Chip
                                key={selectedDocumentTypes[0]}
                                size="small"
                                label={translate(
                                  DOCUMENT_TYPES_TRANSLATION_KEYS[
                                    selectedDocumentTypes[0] as keyof typeof DOCUMENT_TYPES_TRANSLATION_KEYS
                                  ]
                                )}
                              />
                            );
                          }
                          return (
                            <Chip
                              size="small"
                              label={`${
                                selectedDocumentTypes.length
                              } ${translate('labels.selected')}`}
                            />
                          );
                        }}
                        endAdornment={
                          field.value && field.value.length > 0 ? (
                            <InputAdornment
                              position="end"
                              sx={{ marginRight: 2.5 }}
                            >
                              <IconButton
                                size="small"
                                edge="end"
                                onClick={() => field.onChange([])}
                              >
                                <ClearIcon sx={{ fontSize: '1.3rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ) : null
                        }
                      >
                        {Object.values(DocumentType).map((documentType) => (
                          <MenuItem key={documentType} value={documentType}>
                            <Checkbox
                              checked={(field.value || []).includes(
                                documentType
                              )}
                            />
                            <ListItemText
                              primary={translate(
                                DOCUMENT_TYPES_TRANSLATION_KEYS[
                                  documentType as keyof typeof DOCUMENT_TYPES_TRANSLATION_KEYS
                                ]
                              )}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {documentConfig.allowFilterFields.allowReimbursement && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 2
                  }}
                >
                  <Controller
                    name="isReimbursement"
                    control={control}
                    render={({ field }) => (
                      <FormControl size="small" fullWidth>
                        <InputLabel sx={labelSx}>{`${translate(
                          'labels.beneficiaryType'
                        )}`}</InputLabel>
                        <Select
                          {...field}
                          value={isReimbursement}
                          sx={{ textAlign: 'left' }}
                          label={translate('labels.beneficiaryType')}
                          onChange={(e) => {
                            setIsReimbursement(e.target.value as string);
                            field.onChange(e.target.value);
                          }}
                          endAdornment={
                            field.value ? (
                              <InputAdornment
                                position="end"
                                sx={{ marginRight: 2.5 }}
                              >
                                <IconButton
                                  size="small"
                                  edge="end"
                                  onClick={handleClearIsReimbursement}
                                >
                                  <ClearIcon sx={{ fontSize: '1.3rem' }} />
                                </IconButton>
                              </InputAdornment>
                            ) : null
                          }
                        >
                          {REIMBURSEMENT_FILTER_OPTIONS.map((documentType) => (
                            <MenuItem
                              key={translate(documentType.labelKey)}
                              value={documentType.value}
                            >
                              {translate(documentType.labelKey)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
              <Grid
                size={{
                  xs: 12,
                  sm: 2
                }}
              >
                <Controller
                  name="documentNumber"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      label={translate('labels.invoiceNumber')}
                      slotProps={{
                        input: {
                          endAdornment: field.value && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                aria-label="clear text"
                                onClick={() => {
                                  field.onChange('');
                                }}
                                edge="end"
                              >
                                <ClearIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        },

                        inputLabel: {
                          sx: labelSx
                        }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}
              >
                <Box display="flex" gap={1}>
                  <Controller
                    name="amountFrom"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        fullWidth
                        value={field.value}
                        onChange={(
                          e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                        ) => {
                          const newValue = processMoneyInput(
                            e.target.value,
                            field.value || ''
                          );
                          field.onChange(newValue);
                          setShouldShowMinMaxError(
                            getValues('amountTo')
                              ? Number(newValue) >
                                  (Number(getValues('amountTo')) || 0)
                              : false
                          );
                        }}
                        label={translate(
                          isOnAllCompanies
                            ? 'labels.amountFrom'
                            : 'labels.amountFromCompanyCurrency',
                          companyCurrency
                        )}
                        error={shouldShowMinMaxError}
                        slotProps={{
                          input: {
                            endAdornment: field.value && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  aria-label="clear text"
                                  onClick={() => {
                                    field.onChange('');
                                    setShouldShowMinMaxError(false);
                                  }}
                                  edge="end"
                                >
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            )
                          },

                          inputLabel: {
                            sx: labelSx
                          }
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="amountTo"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        fullWidth
                        value={field.value}
                        onChange={(
                          e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                        ) => {
                          const newValue = processMoneyInput(
                            e.target.value,
                            field.value || ''
                          );
                          field.onChange(newValue);
                          setShouldShowMinMaxError(
                            getValues('amountFrom')
                              ? Number(newValue) <
                                  (Number(getValues('amountFrom')) || 0)
                              : false
                          );
                        }}
                        label={translate(
                          isOnAllCompanies
                            ? 'labels.amountTo'
                            : 'labels.amountToCompanyCurrency',
                          companyCurrency
                        )}
                        error={shouldShowMinMaxError}
                        slotProps={{
                          input: {
                            endAdornment: field.value && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    field.onChange('');
                                    setShouldShowMinMaxError(false);
                                  }}
                                  edge="end"
                                >
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            )
                          },

                          inputLabel: {
                            sx: labelSx
                          }
                        }}
                      />
                    )}
                  />
                </Box>
                {shouldShowMinMaxError && (
                  <FormHelperText error={shouldShowMinMaxError}>
                    {translate('errors.amountFromGreaterThanAmountTo')}
                  </FormHelperText>
                )}
              </Grid>
              {documentConfig.allowFilterFields.allowPaymentStatus && (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 2
                    }}
                  >
                    <Controller
                      name="paymentStatuses"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small" fullWidth>
                          <InputLabel sx={labelSx}>
                            {translate('labels.paymentStatus')}
                          </InputLabel>
                          <Select
                            {...field}
                            multiple
                            value={field.value || []}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                            }}
                            sx={{ textAlign: 'left' }}
                            label={translate('labels.paymentStatus')}
                            renderValue={(selected) => {
                              const selectedStatuses = selected;
                              if (selectedStatuses.length === 0) {
                                return null;
                              }
                              if (selectedStatuses.length === 1) {
                                return (
                                  <Chip
                                    key={selectedStatuses[0]}
                                    size="small"
                                    label={translate(
                                      PAYMENT_STATUSES_TRANSLATION_KEYS[
                                        selectedStatuses[0]
                                      ]
                                    )}
                                  />
                                );
                              }
                              return (
                                <Chip
                                  size="small"
                                  label={`${
                                    selectedStatuses.length
                                  } ${translate('labels.selected')}`}
                                />
                              );
                            }}
                            endAdornment={
                              field.value && field.value.length > 0 ? (
                                <InputAdornment
                                  position="end"
                                  sx={{ marginRight: 2.5 }}
                                >
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={() => field.onChange([])}
                                  >
                                    <ClearIcon sx={{ fontSize: '1.3rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              ) : null
                            }
                          >
                            {Object.values(PaymentStatus).map(
                              (paymentStatus) => (
                                <MenuItem
                                  key={paymentStatus}
                                  value={paymentStatus}
                                >
                                  <Checkbox
                                    checked={(field.value || []).includes(
                                      paymentStatus
                                    )}
                                  />
                                  <ListItemText
                                    primary={translate(
                                      PAYMENT_STATUSES_TRANSLATION_KEYS[
                                        paymentStatus
                                      ]
                                    )}
                                  />
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 2
                    }}
                  >
                    <Controller
                      name="paymentChannel"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small" fullWidth>
                          <InputLabel sx={labelSx}>{`${translate(
                            'labels.paymentChannel'
                          )}`}</InputLabel>
                          <Select
                            {...field}
                            sx={{ textAlign: 'left' }}
                            label={translate('labels.paymentChannel')}
                            endAdornment={
                              field.value && (
                                <InputAdornment
                                  position="end"
                                  sx={{ marginRight: 2.5 }}
                                >
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={() => field.onChange('')}
                                  >
                                    <ClearIcon sx={{ fontSize: '1.3rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                          >
                            {Object.values(PaymentChannel).map(
                              (paymentChannel) => (
                                <MenuItem
                                  key={paymentChannel}
                                  value={paymentChannel}
                                >
                                  {translate(
                                    PAYMENT_CHANNELS_TRANSLATION_KEYS[
                                      paymentChannel
                                    ]
                                  )}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </>
              )}
            </LocalizationProvider>
            <Grid container paddingTop={1} gap={2} sx={{ width: '100%' }}>
              <Grid>
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={isApplyButtonDisabled}
                >
                  {translate('buttons.apply')}
                </Button>
              </Grid>
              <Grid>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    reset(documentConfig.defaultFilterValues);
                    setAccountStatus('');
                    setIsReimbursement('');
                    setTags([]);
                    setDateErrors({
                      invoiceDateFrom: '',
                      invoiceDateTo: '',
                      uploadedDateFrom: '',
                      uploadedDateTo: ''
                    });
                    setShouldShowMinMaxError(false);
                    handleApplyFilters(
                      documentConfig.defaultFilterValues as unknown as InvoicesFiltersForm
                    );
                  }}
                >
                  {translate('buttons.reset')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Paper>
  );
};
